import React, { useEffect, useMemo, useState } from "react";
import {
  Table,
  Button,
  Tag,
  message,
  Modal,
  Form,
  Input,
  Popconfirm,
  Tooltip,
  Space,
} from "antd";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../utils/axiosInstance";
import {
  UserAddOutlined,
  FileTextOutlined,
  PlusOutlined,
  DownloadOutlined,
  PrinterOutlined,
  TeamOutlined,
  EditOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import dayjs from "dayjs";

const Invoices = () => {
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const [seller, setSeller] = useState({
    name: "",
    addressLines: [],
    email: "",
    phone: "",
    vat: "",
  });

  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [manageContactsOpen, setManageContactsOpen] = useState(false);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [contactsRows, setContactsRows] = useState([]);
  const [editingContact, setEditingContact] = useState(null);
  const [contactForm] = Form.useForm();

  const formatCurrency = (n) => {
    try {
      return new Intl.NumberFormat("en-GB", {
        style: "currency",
        currency: "GBP",
        maximumFractionDigits: 2,
      }).format(Number(n || 0));
    } catch {
      return `£${Number(n || 0).toFixed(2)}`;
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get("/admin/invoices");
      const list = Array.isArray(data?.invoices)
        ? data.invoices
        : Array.isArray(data)
        ? data
        : [];
      setRows(list);
    } catch (e) {
      message.error(e?.response?.data?.message || "Failed to load invoices");
    } finally {
      setLoading(false);
    }
  };

  const loadSellerInfo = async () => {
    try {
      const { data } = await axiosInstance.get("/admin/invoices/seller");
      const s = data?.seller || data || {};
      const address = (s.address || s.addressText || "")
        .toString()
        .replace(/\r/g, "")
        .split("\n")
        .filter(Boolean);

      setSeller({
        name: s.name || s.companyName || "",
        addressLines: address,
        email: s.email || "",
        phone: s.phone || "",
        vat: s.vat || s.vatNumber || "",
      });
    } catch {
      const lsName = localStorage.getItem("companyName") || "";
      const lsAddr = (localStorage.getItem("companyAddress") || "")
        .split("\n")
        .filter(Boolean);
      const lsVat = localStorage.getItem("companyVat") || "";
      const lsEmail = localStorage.getItem("companyEmail") || "";
      const lsPhone = localStorage.getItem("companyPhone") || "";

      setSeller({
        name: lsName,
        addressLines: lsAddr,
        email: lsEmail,
        phone: lsPhone,
        vat: lsVat,
      });
    }
  };

  const loadContacts = async () => {
    setContactsLoading(true);
    try {
      const { data } = await axiosInstance.get("/admin/invoices/contacts");
      const list = Array.isArray(data?.contacts)
        ? data.contacts
        : Array.isArray(data)
        ? data
        : [];
      setContactsRows(list);
    } catch (e) {
      message.error(e?.response?.data?.message || "Failed to load customers");
      setContactsRows([]);
    } finally {
      setContactsLoading(false);
    }
  };

  useEffect(() => {
    load();
    loadSellerInfo();
  }, []);

  const handleDelete = async (id) => {
    try {
      await axiosInstance.delete(`/admin/invoices/${id}`);
      message.success("Invoice deleted");
      load();
    } catch (e) {
      message.error(e?.response?.data?.message || "Failed to delete invoice");
    }
  };

  const stats = useMemo(() => {
    const total = rows.length;
    const drafts = rows.filter(
      (r) => !r.invoiceNo && !r.invoice_no
        ? true
        : String(r.status || "").toLowerCase() === "draft"
    ).length;

    const sentOrPaid = rows.filter((r) => {
      const val = String(r.status || "").toLowerCase();
      return val === "sent" || val === "paid";
    }).length;

    return { total, drafts, sentOrPaid };
  }, [rows]);

  const fetchInvoiceFull = async (id) => {
    const { data } = await axiosInstance.get(`/admin/invoices/${id}`);
    return data?.invoice || data || {};
  };

  const buildInvoicePdfDoc = (inv) => {
    const invoiceNo = inv.invoiceNo || inv.invoice_no || "INV-###";
    const issueDate = inv.issueDate || inv.issue_date || null;
    const dueDate = inv.dueDate || inv.due_date || null;
    const reference = inv.reference || "";

    const customerName =
      inv.customerName || inv.customer_name || inv.contact?.name || "";
    const billTo = inv.billTo || inv.bill_to || inv.contact?.address || "";
    const customerVat =
      inv.customerVat || inv.customer_vat || inv.contact?.vat || "";

    const items = Array.isArray(inv.items)
      ? inv.items
      : Array.isArray(inv.lines)
      ? inv.lines
      : [];

    const localTotals = items.reduce(
      (acc, l) => {
        const qty = Number(l.quantity ?? l.qty ?? 1);
        const unitPrice = Number(l.unitPrice ?? l.unit_price ?? 0);
        const taxRate = Number(l.taxRate ?? l.tax_rate ?? 0);
        const lineSub = qty * unitPrice;
        acc.subtotal += lineSub;
        acc.tax += (lineSub * taxRate) / 100;
        return acc;
      },
      { subtotal: 0, tax: 0 }
    );

    localTotals.subtotal = Number(localTotals.subtotal.toFixed(2));
    localTotals.tax = Number(localTotals.tax.toFixed(2));
    localTotals.grand = Number((localTotals.subtotal + localTotals.tax).toFixed(2));

    const doc = new jsPDF({ unit: "pt", format: "a4" });
    doc.setProperties({ title: `Invoice ${invoiceNo}`, subject: "Invoice" });

    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const M = 60;
    const G = 18;
    const contentW = pageW - M * 2;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(28);
    doc.text("INVOICE", M, 80);

    const leftW = Math.round(contentW * 0.42);
    const metaW = Math.round(contentW * 0.25);
    const rightW = contentW - leftW - metaW - G * 2;

    const leftX = M;
    const metaX = leftX + leftW + G;
    const rightX = metaX + metaW + G;

    const headerY = 112;

    const boxH = 150;
    doc.setLineWidth(1);
    doc.rect(leftX, headerY, leftW, boxH);

    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.text("Deliver to", leftX + 12, headerY + 18);
    doc.setFont("helvetica", "normal");

    const deliverLinesRaw = [customerName, billTo, customerVat ? `VAT: ${customerVat}` : ""]
      .filter(Boolean)
      .join("\n");

    const deliverLines = doc.splitTextToSize(deliverLinesRaw, leftW - 24);
    let dy = headerY + 40;
    deliverLines.forEach((l) => {
      doc.text(l, leftX + 12, dy);
      dy += 16;
    });

    let y = headerY;
    doc.setFont("helvetica", "bold");
    doc.text("Invoice Date", rightX, y + 10);
    doc.setFont("helvetica", "normal");
    doc.text(issueDate ? dayjs(issueDate).format("DD MMM YYYY") : "-", rightX, y + 28);

    doc.setFont("helvetica", "bold");
    doc.text("Due Date", rightX, y + 52);
    doc.setFont("helvetica", "normal");
    doc.text(dueDate ? dayjs(dueDate).format("DD MMM YYYY") : "-", rightX, y + 70);

    doc.setFont("helvetica", "bold");
    doc.text("Invoice Number", rightX, y + 94);
    doc.setFont("helvetica", "normal");
    doc.text(String(invoiceNo), rightX, y + 112);

    if (reference?.trim()) {
      doc.setFont("helvetica", "bold");
      doc.text("Reference", rightX, y + 136);
      doc.setFont("helvetica", "normal");
      const refLines = doc.splitTextToSize(reference, rightW);
      doc.text(refLines, rightX, y + 154);
    }

    y = headerY;
    doc.setFont("helvetica", "bold");
    const sellerName = doc.splitTextToSize(seller.name || "", metaW);
    if (sellerName.length) doc.text(sellerName, metaX, y + 10);

    doc.setFont("helvetica", "normal");
    let sy = y + (sellerName.length ? 28 : 10);

    const sellerAddr = doc.splitTextToSize((seller.addressLines || []).join("\n"), metaW);
    sellerAddr.forEach((l) => {
      doc.text(l, metaX, sy);
      sy += 16;
    });

    if (seller.email) {
      doc.text(`Email: ${seller.email}`, metaX, sy);
      sy += 16;
    }

    if (seller.phone) {
      doc.text(`Phone: ${seller.phone}`, metaX, sy);
      sy += 16;
    }

    if (seller.vat) {
      doc.setFont("helvetica", "bold");
      doc.text("VAT Number", metaX, sy + 8);
      doc.setFont("helvetica", "normal");
      doc.text(String(seller.vat), metaX, sy + 24);
    }

    autoTable(doc, {
      startY: headerY + boxH + 30,
      head: [["Item", "Description", "Qty", "Unit Price", "Line Total"]],
      body: items.map((ln) => {
        const qty = Number(ln.quantity ?? ln.qty ?? 1);
        const price = Number(ln.unitPrice ?? ln.unit_price ?? 0);
        const lineTotal = qty * price;

        return [
          String(ln.refId ?? ln.ref_id ?? ""),
          ln.description || "",
          qty.toFixed(2),
          formatCurrency(price),
          formatCurrency(lineTotal),
        ];
      }),
      margin: { left: M, right: M },
      styles: { fontSize: 9, cellPadding: 6, overflow: "linebreak" },
      headStyles: { fontStyle: "bold" },
    });

    const y0 = doc.lastAutoTable.finalY + 18;
    const boxW2 = 260;
    const boxX = M + contentW - boxW2;

    doc.setLineWidth(1);
    doc.rect(boxX, y0, boxW2, 104);
    doc.setFont("helvetica", "normal");
    doc.text("Subtotal", boxX + 12, y0 + 24);
    doc.text("Total VAT", boxX + 12, y0 + 44);
    doc.setFont("helvetica", "bold");
    doc.text("Total", boxX + 12, y0 + 74);

    doc.setFont("helvetica", "normal");
    doc.text(formatCurrency(localTotals.subtotal), boxX + boxW2 - 12, y0 + 24, {
      align: "right",
    });
    doc.text(formatCurrency(localTotals.tax), boxX + boxW2 - 12, y0 + 44, {
      align: "right",
    });
    doc.setFont("helvetica", "bold");
    doc.text(formatCurrency(localTotals.grand), boxX + boxW2 - 12, y0 + 74, {
      align: "right",
    });

    const noteText = inv.notes || inv.note ? String(inv.notes || inv.note) : "";

    if (noteText.trim()) {
      const ny = y0 + 104 + 16;
      const nl = doc.splitTextToSize(noteText.trim(), contentW);
      let nyPos = ny;

      nl.forEach((line) => {
        if (nyPos > pageH - M) {
          doc.addPage();
          nyPos = M;
        }
        doc.text(line, M, nyPos);
        nyPos += 16;
      });
    }

    return { doc, invoiceNo };
  };

  const handleDownloadInvoice = async (id, e) => {
    e.stopPropagation();
    try {
      const inv = await fetchInvoiceFull(id);
      const { doc, invoiceNo } = buildInvoicePdfDoc(inv);
      doc.save(`${invoiceNo}.pdf`);
    } catch (err) {
      message.error(err?.response?.data?.message || "Failed to download invoice");
    }
  };

  const handlePrintInvoice = async (id, e) => {
    e.stopPropagation();
    try {
      const inv = await fetchInvoiceFull(id);
      const { doc } = buildInvoicePdfDoc(inv);
      const blob = doc.output("blob");
      const url = URL.createObjectURL(blob);
      const win = window.open(url, "_blank");
      if (win) {
        win.onload = () => {
          win.print();
          setTimeout(() => URL.revokeObjectURL(url), 1000);
        };
      }
    } catch (err) {
      message.error(err?.response?.data?.message || "Failed to print invoice");
    }
  };

  const openAddContact = () => {
    setEditingContact(null);
    contactForm.resetFields();
    setContactModalOpen(true);
  };

  const openManageContacts = async () => {
    setManageContactsOpen(true);
    await loadContacts();
  };

  const openEditContact = (contact) => {
    setEditingContact(contact);
    contactForm.setFieldsValue({
      name: contact.name || "",
      address: contact.address || "",
      email: contact.email || "",
      vat: contact.vat || "",
      phone: contact.phone || "",
    });
    setContactModalOpen(true);
  };

  const saveContact = async () => {
    try {
      const vals = await contactForm.validateFields();

      const payload = {
        name: vals.name?.trim(),
        address: vals.address?.trim() || "",
        email: vals.email?.trim() || "",
        vat: vals.vat?.trim() || "",
        phone: vals.phone?.trim() || "",
      };

      if (editingContact?.id) {
        await axiosInstance.put(`/admin/invoices/contacts/${editingContact.id}`, payload);
        message.success("Customer updated");
      } else {
        await axiosInstance.post("/admin/invoices/contacts", payload);
        message.success("Customer created");
      }

      setContactModalOpen(false);
      setEditingContact(null);
      contactForm.resetFields();
      await loadContacts();
    } catch (e) {
      message.error(e?.response?.data?.message || "Failed to save customer");
    }
  };

  const handleDeleteContact = async (id) => {
    try {
      await axiosInstance.delete(`/admin/invoices/contacts/${id}`);
      message.success("Customer deleted");
      await loadContacts();
    } catch (e) {
      message.error(e?.response?.data?.message || "Failed to delete customer");
    }
  };

  const columns = [
    {
      title: "Invoice #",
      dataIndex: "invoiceNo",
      key: "num",
      render: (v, r) =>
        v || r?.invoice_no ? (
          <span className="font-medium text-gray-900">{v || r?.invoice_no}</span>
        ) : (
          <Tag color="default">DRAFT</Tag>
        ),
    },
    {
      title: "Issue Date",
      dataIndex: "issueDate",
      key: "i",
      render: (v, r) => (
        <span className="text-gray-700">{v || r?.issue_date || "-"}</span>
      ),
    },
    {
      title: "Customer",
      dataIndex: "customerName",
      key: "c",
      render: (v, r) => (
        <span className="font-medium text-gray-900">
          {v || r?.customer_name || r?.customer?.name || "-"}
        </span>
      ),
    },
    {
      title: "Total (£)",
      key: "t",
      render: (_, r) => (
        <span className="font-semibold text-gray-900">
          {Number(r.grandTotal ?? r.grand_total ?? 0).toFixed(2)}
        </span>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "s",
      render: (v) => {
        const val = (v || "draft").toString().toLowerCase();
        return (
          <Tag
            color={
              val === "archived"
                ? "orange"
                : val === "sent" || val === "paid"
                ? "green"
                : "default"
            }
          >
            {val.toUpperCase()}
          </Tag>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: 280,
      render: (_, record) => {
        return (
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <Button
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/invoices/${record.id}`);
              }}
              className="rounded-lg"
            >
              Edit
            </Button>

            <Tooltip title="Download Invoice">
              <Button
                icon={<DownloadOutlined />}
                onClick={(e) => handleDownloadInvoice(record.id, e)}
                className="rounded-lg"
              />
            </Tooltip>

            <Tooltip title="Print Invoice">
              <Button
                icon={<PrinterOutlined />}
                onClick={(e) => handlePrintInvoice(record.id, e)}
                className="rounded-lg"
              />
            </Tooltip>

            <Popconfirm
              title="Delete this invoice? This action is permanent."
              onConfirm={async (e) => {
                if (e && e.stopPropagation) e.stopPropagation();
                await handleDelete(record.id);
              }}
              onCancel={(e) => e && e.stopPropagation()}
              okText="Delete"
              cancelText="Cancel"
            >
              <Button
                danger
                className="rounded-lg"
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                Delete
              </Button>
            </Popconfirm>
          </div>
        );
      },
    },
  ];

  const contactColumns = [
    {
      title: "Name / Company",
      dataIndex: "name",
      key: "name",
      render: (v) => <span className="font-medium text-gray-900">{v || "-"}</span>,
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      render: (v) => <span className="text-gray-700">{v || "-"}</span>,
    },
    {
      title: "Phone",
      dataIndex: "phone",
      key: "phone",
      render: (v) => <span className="text-gray-700">{v || "-"}</span>,
    },
    {
      title: "VAT",
      dataIndex: "vat",
      key: "vat",
      render: (v) => <span className="text-gray-700">{v || "-"}</span>,
    },
    {
      title: "Address",
      dataIndex: "address",
      key: "address",
      render: (v) => (
        <div className="text-gray-700 whitespace-pre-wrap max-w-[300px]">
          {v || "-"}
        </div>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 160,
      render: (_, record) => (
        <Space>
          <Tooltip title="Edit Customer">
            <Button
              icon={<EditOutlined />}
              onClick={() => openEditContact(record)}
              className="rounded-lg"
            />
          </Tooltip>

          <Popconfirm
            title="Delete this customer?"
            onConfirm={() => handleDeleteContact(record.id)}
            okText="Delete"
            cancelText="Cancel"
          >
            <Tooltip title="Delete Customer">
              <Button danger icon={<DeleteOutlined />} className="rounded-lg" />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 py-6 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Invoices</h1>
              <p className="text-gray-600">
                Manage customer invoices, drafts and billing records
              </p>
            </div>

            <div className="flex flex-wrap gap-2">
              <Button
                icon={<TeamOutlined />}
                onClick={openManageContacts}
                className="rounded-lg font-medium"
              >
                Manage Customers
              </Button>

              <Button
                icon={<UserAddOutlined />}
                onClick={openAddContact}
                className="rounded-lg font-medium"
              >
                New Customer
              </Button>

              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => navigate("/invoices/manage")}
                className="bg-blue-500 hover:bg-blue-600 border-blue-500 text-white rounded-lg font-medium"
              >
                Create Invoice
              </Button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5">
            <div className="text-sm text-gray-500 mb-1">Total Invoices</div>
            <div className="text-2xl font-bold text-blue-600">{stats.total}</div>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5">
            <div className="text-sm text-gray-500 mb-1">Draft Invoices</div>
            <div className="text-2xl font-bold text-orange-500">{stats.drafts}</div>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5">
            <div className="text-sm text-gray-500 mb-1">Sent / Paid</div>
            <div className="text-2xl font-bold text-green-600">{stats.sentOrPaid}</div>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="p-6 border-b border-gray-200 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
              <FileTextOutlined className="text-blue-600 text-lg" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900 m-0">Invoice List</h2>
              <p className="text-sm text-gray-500 m-0">
                Click any row to open and edit invoice details
              </p>
            </div>
          </div>

          <Table
            rowKey="id"
            dataSource={rows}
            columns={columns}
            loading={loading}
            bordered={false}
            size="middle"
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} invoices`,
              className: "px-6 py-4",
              pageSizeOptions: ["10", "20", "50", "100"],
            }}
            onRow={(record) => ({
              onClick: () => navigate(`/invoices/${record.id}`),
              className: "clickable-row",
            })}
            className="modern-invoices-table"
            locale={{
              emptyText: (
                <div className="text-center py-10">
                  <div className="text-lg font-medium text-gray-500 mb-2">No invoices found</div>
                  <div className="text-sm text-gray-400">
                    Create your first invoice to get started
                  </div>
                </div>
              ),
            }}
          />
        </div>
      </div>

      <Modal
        title={
          <div className="flex items-center space-x-2">
            {editingContact ? (
              <>
                <EditOutlined className="text-blue-500" />
                <span>Edit Customer</span>
              </>
            ) : (
              <>
                <UserAddOutlined className="text-blue-500" />
                <span>Add Customer</span>
              </>
            )}
          </div>
        }
        open={contactModalOpen}
        zIndex={2000}
        onCancel={() => {
          setContactModalOpen(false);
          setEditingContact(null);
          contactForm.resetFields();
        }}
        onOk={saveContact}
        okText={editingContact ? "Update Customer" : "Save Customer"}
        className="modern-modal"
      >
        <Form form={contactForm} layout="vertical">
          <Form.Item
            label="Name / Company"
            name="name"
            rules={[{ required: true, message: "Please enter a name" }]}
          >
            <Input className="rounded-lg" />
          </Form.Item>

          <Form.Item label="Address" name="address">
            <Input.TextArea rows={3} className="rounded-lg" />
          </Form.Item>

          <Form.Item
            label="Email"
            name="email"
            rules={[
              {
                type: "email",
                message: "Please enter a valid email address",
              },
            ]}
          >
            <Input placeholder="Enter email address" className="rounded-lg" />
          </Form.Item>

          <Form.Item label="Phone" name="phone">
            <Input placeholder="Enter phone number" className="rounded-lg" />
          </Form.Item>

          <Form.Item label="VAT Number" name="vat">
            <Input className="rounded-lg" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={
          <div className="flex items-center space-x-2">
            <TeamOutlined className="text-blue-500" />
            <span>Manage Customers</span>
          </div>
        }
        open={manageContactsOpen}
        onCancel={() => setManageContactsOpen(false)}
        footer={[
          <Button
            key="new"
            type="primary"
            icon={<UserAddOutlined />}
            onClick={openAddContact}
            className="bg-blue-500 hover:bg-blue-600 border-blue-500 text-white rounded-lg"
          >
            Add Customer
          </Button>,
          <Button key="close" onClick={() => setManageContactsOpen(false)} className="rounded-lg">
            Close
          </Button>,
        ]}
        width={1100}
        className="modern-modal"
      >
        <Table
          rowKey="id"
          dataSource={contactsRows}
          columns={contactColumns}
          loading={contactsLoading}
          pagination={{
            pageSize: 8,
            showSizeChanger: true,
            pageSizeOptions: ["8", "15", "30", "50"],
          }}
          className="modern-invoices-table"
          locale={{
            emptyText: (
              <div className="text-center py-8">
                <div className="text-lg font-medium text-gray-500 mb-2">
                  No customers found
                </div>
                <div className="text-sm text-gray-400">
                  Add your first customer to get started
                </div>
              </div>
            ),
          }}
        />
      </Modal>

      <style>{`
        .clickable-row td {
          cursor: pointer;
        }

        .modern-invoices-table .ant-table-thead > tr > th {
          background: #f8fafc !important;
          color: #374151 !important;
          font-weight: 600;
          border-bottom: 2px solid #e5e7eb !important;
          white-space: nowrap;
          padding: 12px 16px !important;
        }

        .modern-invoices-table .ant-table-tbody > tr > td {
          border-bottom: 1px solid #f1f5f9 !important;
          padding: 12px 16px !important;
          transition: all 0.2s ease;
          background: #fff;
        }

        .modern-invoices-table .ant-table-tbody > tr.clickable-row:hover > td {
          background: #f9fafb !important;
        }

        .modern-invoices-table .ant-table-container {
          border: none !important;
        }

        .modern-modal .ant-modal-content {
          border-radius: 16px;
          overflow: hidden;
        }

        .modern-modal .ant-modal-header {
          border-bottom: 1px solid #e5e7eb;
          background: #f8fafc;
        }
      `}</style>
    </div>
  );
};

export default Invoices;